import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { materialCategoryService } from '../../../services/materialCategoryService';

const CategoryTree = ({
  categories,
  selectedCategory,
  expandedCategories,
  onCategorySelect,
  onCategoryExpand,
  onCategoryEdit,
  onCategoryDelete
}) => {
  const [materialCounts, setMaterialCounts] = useState({});

  useEffect(() => {
    loadMaterialCounts();
  }, [categories]);

  const loadMaterialCounts = async () => {
    const counts = {};
    for (const category of categories || []) {
      const { data } = await materialCategoryService?.getMaterialCountByCategory(category?.id);
      counts[category?.id] = data || 0;
    }
    setMaterialCounts(counts);
  };

  const getLevelColor = (level) => {
    const colors = [
      'var(--color-primary)',
      'var(--color-success)',
      'var(--color-warning)',
      'var(--color-error)',
      'var(--color-muted-foreground)',
      'var(--color-primary)'
    ];
    return colors?.[level] || colors?.[0];
  };

  const renderCategoryNode = (category, level = 0) => {
    const isExpanded = expandedCategories?.includes(category?.id);
    const isSelected = selectedCategory?.id === category?.id;
    const hasChildren = category?.children && category?.children?.length > 0;
    const materialCount = materialCounts?.[category?.id] || 0;

    return (
      <div key={category?.id} className="w-full">
        <div
          className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-all duration-250 hover:bg-muted ${
            isSelected ? 'bg-primary/10 border-l-2 border-primary' : ''
          }`}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
        >
          {hasChildren && (
            <button
              onClick={(e) => {
                e?.stopPropagation();
                onCategoryExpand(category?.id);
              }}
              className="flex-shrink-0 w-5 h-5 flex items-center justify-center hover:bg-muted rounded transition-colors duration-250"
            >
              <Icon
                name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
                size={16}
                color="var(--color-muted-foreground)"
              />
            </button>
          )}
          {!hasChildren && <div className="w-5" />}

          <div
            className="flex-1 min-w-0 flex items-center gap-2"
            onClick={() => onCategorySelect(category)}
          >
            <div
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: getLevelColor(category?.level) }}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{category?.name}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-muted-foreground">Level {category?.level}</span>
                {materialCount > 0 && (
                  <>
                    <span className="text-xs text-muted-foreground">•</span>
                    <span className="text-xs text-muted-foreground">{materialCount} materials</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={(e) => {
                e?.stopPropagation();
                onCategoryEdit(category);
              }}
              className="p-1.5 hover:bg-muted rounded transition-colors duration-250"
              title="Edit category"
            >
              <Icon name="Edit2" size={14} color="var(--color-muted-foreground)" />
            </button>
            <button
              onClick={(e) => {
                e?.stopPropagation();
                onCategoryDelete(category?.id);
              }}
              className="p-1.5 hover:bg-destructive/10 rounded transition-colors duration-250"
              title="Delete category"
            >
              <Icon name="Trash2" size={14} color="var(--color-destructive)" />
            </button>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className="mt-1">
            {category?.children?.map(child => renderCategoryNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (!categories || categories?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <Icon name="Layers" size={48} color="var(--color-muted-foreground)" />
        <p className="mt-4 text-sm text-muted-foreground text-center">
          No categories found. Create your first category to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="p-2">
      {categories?.map(category => renderCategoryNode(category))}
    </div>
  );
};

export default CategoryTree;
